import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Shield, Store, Users, LogOut, Loader2, TrendingUp,
  CreditCard, Receipt, Settings, FileWarning, MessageSquare,
  UserPlus, DollarSign, UserCog, ChevronLeft, ChevronRight,
  AlertTriangle, Bell, BarChart3, Sparkles, Phone, SmilePlus, Ban
} from "lucide-react";
import ClearCacheButton from "@/components/ClearCacheButton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import ThemeToggle from "@/components/ThemeToggle";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";

// Block search engine indexing for admin pages
const useNoIndex = () => {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);
};

import AdminOverviewTab from "./admin/AdminOverviewTab";
import AdminRestaurantsTab from "./admin/AdminRestaurantsTab";
import AdminPlansTab from "./admin/AdminPlansTab";
import AdminSubscriptionsTab from "./admin/AdminSubscriptionsTab";
import AdminUsersTab from "./admin/AdminUsersTab";
import AdminPlatformTab from "./admin/AdminPlatformTab";
import AdminAuditTab from "./admin/AdminAuditTab";
import AdminSupportTab from "./admin/AdminSupportTab";
import AdminAffiliatesTab from "./admin/AdminAffiliatesTab";
import AdminPaymentConfigTab from "./admin/AdminPaymentConfigTab";
import AdminStaffTab from "./admin/AdminStaffTab";
import AdminReviewTab from "./admin/AdminReviewTab";
import AdminTrafficTab from "./admin/AdminTrafficTab";
import AdminChangelogTab from "./admin/AdminChangelogTab";
import AdminNotificationsTab from "./admin/AdminNotificationsTab";
import AdminAccountsReportTab from "./admin/AdminAccountsReportTab";
import AdminMarketingTab from "./admin/AdminMarketingTab";
import AdminVisitorChatTab from "./admin/AdminVisitorChatTab";
import AdminWhatsAppTab from "./admin/AdminWhatsAppTab";
import AdminNpsTab from "./admin/AdminNpsTab";
import AdminSeoTab from "./admin/AdminSeoTab";
import AdminIpBansTab from "./admin/AdminIpBansTab";

type Restaurant = Tables<"restaurants">;

interface Plan {
  id: string; name: string; slug: string; description: string | null;
  price_monthly: number; price_yearly: number | null; billing_type: string;
  max_orders_per_month: number | null; max_products: number | null;
  max_categories: number | null; features: any; trial_days: number | null;
  is_active: boolean; is_featured: boolean; sort_order: number;
  created_at: string; updated_at: string;
}

type TabKey = "overview" | "restaurants" | "users" | "plans" | "subscriptions" | "payment" | "platform" | "support" | "visitor_chat" | "whatsapp" | "review" | "staff" | "affiliates" | "audit" | "traffic" | "changelog" | "notifications" | "accounts_report" | "marketing" | "nps" | "seo" | "ip_bans";

interface NavSection {
  title: string;
  items: { key: TabKey; label: string; icon: any; badge?: number }[];
}

const AdminDashboard = () => {
  useNoIndex();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [profiles, setProfiles] = useState<any[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<TabKey>("overview");
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [flaggedCount, setFlaggedCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const fetchData = useCallback(async (isInitial = false) => {
    if (isInitial) {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate("/admin/login"); return; }

      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "super_admin")
        .maybeSingle();

      if (!roleData) {
        await supabase.auth.signOut();
        navigate("/admin/login");
        return;
      }

      setCurrentUserId(user.id);
    }

    const [restRes, ordersRes, profilesRes, plansRes, subsRes, flaggedRes] = await Promise.all([
      supabase.from("restaurants").select("*").order("created_at", { ascending: false }),
      supabase.from("orders").select("*").order("created_at", { ascending: false }).limit(100),
      supabase.from("profiles").select("*").order("created_at", { ascending: false }),
      supabase.from("plans").select("*").order("sort_order", { ascending: true }),
      supabase.from("subscriptions").select("*").order("created_at", { ascending: false }),
      supabase.from("flagged_restaurants").select("id").eq("status", "pending"),
    ]);

    setRestaurants(restRes.data || []);
    setOrders(ordersRes.data || []);
    setProfiles(profilesRes.data || []);
    setPlans((plansRes.data as any[])?.map(p => ({ ...p, features: p.features ?? {} })) || []);
    setSubscriptions(subsRes.data || []);
    setFlaggedCount(flaggedRes.data?.length || 0);
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    fetchData(true);
    const interval = setInterval(() => fetchData(false), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const logAction = useCallback(async (action: string, entityType: string, entityId: string, details?: any) => {
    await supabase.from("audit_logs").insert({
      user_id: currentUserId,
      action,
      entity_type: entityType,
      entity_id: entityId,
      details: details || {},
      severity: "info",
    });
  }, [currentUserId]);

  const handleLogout = async () => {
    await logAction("admin_logout", "auth", currentUserId || "");
    await supabase.auth.signOut();
    navigate("/admin/login");
  };

  const handleImpersonate = (restaurantId: string) => {
    sessionStorage.setItem("admin_impersonate_restaurant_id", restaurantId);
    sessionStorage.setItem("admin_return_path", "/admin");
    logAction("admin_impersonate", "restaurant", restaurantId);
    navigate("/dashboard");
    toast({ title: "Conectado à loja", description: "Você está visualizando como administrador da loja." });
  };

  const navSections: NavSection[] = [
    {
      title: "Principal",
      items: [
        { key: "overview", label: "Visão Geral", icon: TrendingUp },
        { key: "traffic", label: "Tráfego", icon: BarChart3 },
        { key: "restaurants", label: "Restaurantes", icon: Store },
        { key: "users", label: "Usuários", icon: Users },
        { key: "accounts_report", label: "Relatório Contas", icon: BarChart3 },
      ],
    },
    {
      title: "Financeiro",
      items: [
        { key: "plans", label: "Planos", icon: CreditCard },
        { key: "subscriptions", label: "Assinaturas", icon: Receipt },
        { key: "payment", label: "Pagamentos", icon: DollarSign },
      ],
    },
    {
      title: "Operacional",
      items: [
        { key: "nps", label: "NPS ViaMenu", icon: SmilePlus },
        { key: "support", label: "Suporte", icon: MessageSquare },
        { key: "visitor_chat", label: "Chat Visitantes", icon: MessageSquare },
        { key: "whatsapp", label: "WhatsApp", icon: Phone },
        { key: "notifications", label: "Notificações", icon: Bell },
        { key: "review", label: "Revisão", icon: AlertTriangle, badge: flaggedCount },
        { key: "staff", label: "Equipe", icon: UserCog },
        { key: "affiliates", label: "Afiliados", icon: UserPlus },
      ],
    },
    {
      title: "Marketing",
      items: [
        { key: "marketing", label: "Marketing IA", icon: Sparkles },
        { key: "seo", label: "SEO", icon: BarChart3 },
      ],
    },
    {
      title: "Sistema",
      items: [
        { key: "ip_bans", label: "IPs Banidos", icon: Ban },
        { key: "platform", label: "Plataforma", icon: Settings },
        { key: "changelog", label: "Changelog", icon: Sparkles },
        { key: "audit", label: "Auditoria", icon: FileWarning },
      ],
    },
  ];

  const activeLabel = navSections.flatMap(s => s.items).find(i => i.key === activeTab)?.label || "";

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-primary mx-auto flex items-center justify-center">
            <Shield className="h-6 w-6 text-primary-foreground" />
          </div>
          <Loader2 className="h-5 w-5 animate-spin text-primary mx-auto" />
          <p className="text-xs text-muted-foreground">Carregando painel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:sticky top-0 left-0 z-50 h-screen flex flex-col
          bg-card border-r border-border transition-all duration-300 ease-in-out
          ${sidebarCollapsed ? "w-[68px]" : "w-64"}
          ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
      >
        {/* Logo */}
        <div className={`h-14 flex items-center border-b border-border shrink-0 ${sidebarCollapsed ? "justify-center px-2" : "px-4 gap-3"}`}>
          <div className="w-8 h-8 rounded-lg bg-gradient-primary flex items-center justify-center shrink-0">
            <Shield className="h-4 w-4 text-primary-foreground" />
          </div>
          {!sidebarCollapsed && (
            <div className="min-w-0">
              <h1 className="font-display font-bold text-sm truncate">ViaMenu</h1>
              <p className="text-[9px] text-muted-foreground">Super Admin</p>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-4">
          {navSections.map((section) => (
            <div key={section.title}>
              {!sidebarCollapsed && (
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold px-2 mb-1.5">
                  {section.title}
                </p>
              )}
              <div className="space-y-0.5">
                {section.items.map((item) => (
                  <button
                    key={item.key}
                    onClick={() => { setActiveTab(item.key); setMobileMenuOpen(false); }}
                    title={sidebarCollapsed ? item.label : undefined}
                    className={`
                      w-full flex items-center gap-2.5 rounded-lg text-sm font-medium transition-all
                      ${sidebarCollapsed ? "justify-center p-2.5" : "px-3 py-2"}
                      ${activeTab === item.key
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                      }
                    `}
                  >
                    <item.icon className="h-4 w-4 shrink-0" />
                    {!sidebarCollapsed && (
                      <>
                        <span className="truncate flex-1 text-left">{item.label}</span>
                        {item.badge && item.badge > 0 && (
                          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-[9px] h-5 min-w-5 flex items-center justify-center">
                            {item.badge}
                          </Badge>
                        )}
                      </>
                    )}
                    {sidebarCollapsed && item.badge && item.badge > 0 && (
                      <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-amber-500" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Collapse toggle */}
        <div className="border-t border-border p-2 shrink-0 hidden lg:block">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
          >
            {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <><ChevronLeft className="h-4 w-4" /><span>Recolher</span></>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="h-14 border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-30 flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-1.5 rounded-md hover:bg-secondary/60 text-muted-foreground"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div>
              <h2 className="font-display font-semibold text-sm">{activeLabel}</h2>
              <p className="text-[10px] text-muted-foreground hidden sm:block">Painel administrativo da plataforma</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {flaggedCount > 0 && (
              <button
                onClick={() => setActiveTab("review")}
                className="relative p-2 rounded-lg hover:bg-secondary/60 text-muted-foreground transition-colors"
              >
                <Bell className="h-4 w-4" />
                <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              </button>
            )}
            <ClearCacheButton />
            <ThemeToggle />
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-muted-foreground text-xs">
              <LogOut className="h-4 w-4 mr-1.5" />
              <span className="hidden sm:inline">Sair</span>
            </Button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-7xl mx-auto">
            {activeTab === "overview" && (
              <AdminOverviewTab restaurants={restaurants} orders={orders} profiles={profiles} subscriptions={subscriptions} />
            )}
            {activeTab === "restaurants" && (
              <AdminRestaurantsTab restaurants={restaurants} setRestaurants={setRestaurants} orders={orders} plans={plans} logAction={logAction} onImpersonate={handleImpersonate} />
            )}
            {activeTab === "plans" && (
              <AdminPlansTab plans={plans} setPlans={setPlans} logAction={logAction} />
            )}
            {activeTab === "payment" && (
              <AdminPaymentConfigTab logAction={logAction} />
            )}
            {activeTab === "subscriptions" && (
              <AdminSubscriptionsTab subscriptions={subscriptions} restaurants={restaurants} plans={plans} />
            )}
            {activeTab === "users" && (
              <AdminUsersTab profiles={profiles} onRefresh={() => fetchData(false)} />
            )}
            {activeTab === "staff" && (
              <AdminStaffTab logAction={logAction} currentUserId={currentUserId} />
            )}
            {activeTab === "support" && (
              <AdminSupportTab restaurants={restaurants} currentUserId={currentUserId} />
            )}
            {activeTab === "visitor_chat" && (
              <AdminVisitorChatTab currentUserId={currentUserId} />
            )}
            {activeTab === "review" && (
              <AdminReviewTab logAction={logAction} currentUserId={currentUserId} />
            )}
            {activeTab === "affiliates" && (
              <AdminAffiliatesTab restaurants={restaurants} logAction={logAction} />
            )}
            {activeTab === "platform" && (
              <AdminPlatformTab logAction={logAction} />
            )}
            {activeTab === "audit" && (
              <AdminAuditTab />
            )}
            {activeTab === "traffic" && (
              <AdminTrafficTab />
            )}
            {activeTab === "changelog" && (
              <AdminChangelogTab />
            )}
            {activeTab === "notifications" && (
              <AdminNotificationsTab restaurants={restaurants} logAction={logAction} />
            )}
            {activeTab === "accounts_report" && (
              <AdminAccountsReportTab restaurants={restaurants} plans={plans} orders={orders} subscriptions={subscriptions} />
            )}
            {activeTab === "marketing" && (
              <AdminMarketingTab />
            )}
            {activeTab === "whatsapp" && (
              <AdminWhatsAppTab restaurants={restaurants} />
            )}
            {activeTab === "nps" && (
              <AdminNpsTab restaurants={restaurants} />
            )}
            {activeTab === "seo" && (
              <AdminSeoTab />
            )}
            {activeTab === "ip_bans" && (
              <AdminIpBansTab />
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
